import { PumpSizingProvider, usePumpSizing } from "../context/PumpSizingContext";
import { usePumpSizingPdfDownload } from "../hooks/usePumpSizingPdfDownload";
import { Button } from "./shared/Button";
import { Layout } from "./shared/Layout";
import { StepContent } from "./steps";
import { SavedResultsPanel } from "./steps/pumpcurve/SavedResultsPanel";
import { SaveResultsButton } from "./steps/pumpcurve/SaveResultsButton";

const HEADER_DESCRIPTION = "Find the right Liberty pump for your application. Select an application type, then enter your flow rate (GPM) and total dynamic head (TDH) to identify suitable pumps and review their performance curves.";
const CONTACT_FORM_PATH = "/pump-sizing-contact-form/";

function getContactFormUrl(): string {
	const localized = window.pumpSizingSettings?.contactFormUrl?.trim();
	return localized || CONTACT_FORM_PATH;
}

function isPdfDownloadEnabled(): boolean {
	return window.pumpSizingSettings?.pdfDownloadEnabled === "true";
}

function PumpSizingWizard() {
	const {
		currentStep,
		totalSteps,
		formData,
		nextStep,
		prevStep,
		canGoNext,
		stepError,
		isNextLoading,
		isSavingResults,
		isResultsLoading,
		chartData,
		savedSubmissionUuid,
		saveResults,
	} = usePumpSizing();
	const { downloadPdf, isPdfDownloading, pdfError } = usePumpSizingPdfDownload();

	const isLastStep = currentStep === totalSteps;
	const isEmailApplication = formData.applicationType === "email";
	const contactFormUrl = getContactFormUrl();
	const canDownloadPdf = isPdfDownloadEnabled();
	const isBusy =
		isPdfDownloading || isSavingResults || isResultsLoading;
	const isPdfDownloadDisabled = isBusy || chartData === null;
	const hasSavedResults = savedSubmissionUuid !== "";
	const hasChartContent =
		chartData !== null &&
		((chartData.series ?? []).length > 0 ||
			(chartData.system_curve_points ?? []).length > 0);

	const saveAndDownloadPdf = async () => {
		const saved = await saveResults();
		if (!saved) {
			return;
		}
		// Give React one tick to commit the saved-result-ID badge to the DOM
		// before we scrape the page for the PDF.
		await new Promise((resolve) => {
			requestAnimationFrame(() => requestAnimationFrame(resolve));
		});
		const uuidEl = document.querySelector(".pf-saved-result-id__value");
		const uuid = uuidEl?.textContent?.trim() ?? "";
		await downloadPdf(uuid || undefined);
	};

	const navButtons = (
		<div className="pf-layout__footer-actions">
			{currentStep > 1 ? (
				<Button
					variant="secondary"
					onClick={prevStep}
					disabled={isNextLoading || isPdfDownloading}
				>
					<span aria-hidden="true">←</span>
					<span>Back</span>
				</Button>
			) : null}
			{isLastStep && !isEmailApplication && canDownloadPdf && hasSavedResults && hasChartContent ? (
				<Button
					type="button"
					onClick={() => downloadPdf(savedSubmissionUuid || undefined)}
					disabled={isPdfDownloadDisabled}
				>
					{isPdfDownloading ? "Downloading…" : "Download PDF"}
				</Button>
			) : null}
			{isLastStep && !isEmailApplication && canDownloadPdf && !hasSavedResults && hasChartContent ? (
				<Button
					type="button"
					onClick={() => {
						void saveAndDownloadPdf();
					}}
					disabled={isPdfDownloadDisabled}
				>
					{isSavingResults
						? "Saving…"
						: isPdfDownloading
							? "Downloading…"
							: "Save & Download PDF"}
				</Button>
			) : null}
			{isEmailApplication ? (
				<Button href={contactFormUrl}>
					<span>Contact Us</span>
					<span aria-hidden="true">→</span>
				</Button>
			) : isLastStep ? (
				hasSavedResults && hasChartContent ? <SaveResultsButton /> : null
			) : (
				<Button
					onClick={() => {
						void nextStep();
					}}
					disabled={!canGoNext || isNextLoading}
				>
					<span>{isNextLoading ? "Loading…" : "Next"}</span>
					{!isNextLoading ? (
						<span aria-hidden="true">→</span>
					) : null}
				</Button>
			)}
		</div>
	);

	return (
		<Layout
			title="Pump Sizing"
			description={HEADER_DESCRIPTION}
			topNav={navButtons}
			footer={
				<>
					{stepError ? (
						<p className="pf-form-error" role="alert">
							{stepError}
						</p>
					) : null}
					{canDownloadPdf && pdfError ? (
						<p className="pf-form-error" role="alert">
							{pdfError}
						</p>
					) : null}
					{navButtons}
					{isLastStep ? <SavedResultsPanel /> : null}
				</>
			}
		>
			<StepContent />
		</Layout>
	);
}

export function PumpSizing() {
	return (
		<PumpSizingProvider>
			<PumpSizingWizard />
		</PumpSizingProvider>
	);
}
